import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useAccount } from './AccountContext';
import {
  getUserWorkspaces,
  getWorkspace,
  createWorkspace as createWorkspaceService,
  updateWorkspace as updateWorkspaceService,
  deleteWorkspace as deleteWorkspaceService,
  setDefaultWorkspace
} from '../services/workspaceService';
import { migrateUserToWorkspaces, needsMigration } from '../services/migrationService';
import { WORKSPACE_ALL } from '../constants/workspaces';

const WorkspaceContext = createContext();

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
}

export function WorkspaceProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const { account } = useAccount();

  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(null);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [userWorkspaces, setUserWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);

  // Computed property
  const isAllBrandsView = currentWorkspaceId === WORKSPACE_ALL;

  // Load workspaces on mount and when user/account changes
  useEffect(() => {
    if (currentUser && userProfile && account) {
      loadWorkspaces();
    } else {
      setLoading(false);
    }
  }, [currentUser, userProfile, account]);

  // Load current workspace details when currentWorkspaceId changes
  useEffect(() => {
    if (currentWorkspaceId && currentWorkspaceId !== WORKSPACE_ALL) {
      loadCurrentWorkspace();
    } else if (currentWorkspaceId === WORKSPACE_ALL) {
      setCurrentWorkspace(null); // Clear when viewing all brands
    }
  }, [currentWorkspaceId]);

  async function loadWorkspaces() {
    try {
      setLoading(true);

      // Check if user needs migration
      const shouldMigrate = await needsMigration(currentUser.uid);
      if (shouldMigrate) {
        console.log('Migrating user to workspace structure...');
        await migrateUserToWorkspaces(currentUser.uid, userProfile);
      }

      // Load all user workspaces
      const workspaces = await getUserWorkspaces(currentUser.uid);
      setUserWorkspaces(workspaces);

      // Set initial workspace
      if (workspaces.length > 0) {
        // Use saved workspace from profile, localStorage, or default to first
        const savedWorkspaceId =
          userProfile?.currentWorkspaceId ||
          localStorage.getItem('lastWorkspaceId') ||
          workspaces[0].id;

        // Verify workspace exists
        const workspaceExists = workspaces.some(ws => ws.id === savedWorkspaceId);
        setCurrentWorkspaceId(workspaceExists ? savedWorkspaceId : workspaces[0].id);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading workspaces:', error);
      setLoading(false);
    }
  }

  async function loadCurrentWorkspace() {
    try {
      const workspace = await getWorkspace(currentWorkspaceId);
      setCurrentWorkspace(workspace);
    } catch (error) {
      console.error('Error loading current workspace:', error);
    }
  }

  async function switchWorkspace(workspaceId) {
    try {
      setCurrentWorkspaceId(workspaceId);

      // Persist to localStorage
      localStorage.setItem('lastWorkspaceId', workspaceId);

      // Persist to user profile (skip for ALL_BRANDS)
      if (workspaceId !== WORKSPACE_ALL) {
        await setDefaultWorkspace(currentUser.uid, workspaceId);
      }
    } catch (error) {
      console.error('Error switching workspace:', error);
      throw error;
    }
  }

  function switchToAllBrands() {
    switchWorkspace(WORKSPACE_ALL);
  }

  async function createWorkspace(brandData) {
    try {
      // Pass accountId to workspace creation
      const workspaceId = await createWorkspaceService(currentUser.uid, account?.id, brandData);

      // Refresh workspaces list
      await refreshWorkspaces();

      // Switch to new workspace
      await switchWorkspace(workspaceId);

      return workspaceId;
    } catch (error) {
      console.error('Error creating workspace:', error);
      throw error;
    }
  }

  async function updateWorkspace(workspaceId, updates) {
    try {
      await updateWorkspaceService(workspaceId, updates, currentUser.uid);

      // Refresh workspaces
      await refreshWorkspaces();

      // Refresh current workspace if it was updated
      if (workspaceId === currentWorkspaceId) {
        await loadCurrentWorkspace();
      }
    } catch (error) {
      console.error('Error updating workspace:', error);
      throw error;
    }
  }

  async function deleteWorkspace(workspaceId) {
    try {
      await deleteWorkspaceService(workspaceId, currentUser.uid);

      // Refresh workspaces
      await refreshWorkspaces();

      // Switch to first available workspace if deleted current
      if (workspaceId === currentWorkspaceId) {
        const workspaces = await getUserWorkspaces(currentUser.uid);
        if (workspaces.length > 0) {
          await switchWorkspace(workspaces[0].id);
        }
      }
    } catch (error) {
      console.error('Error deleting workspace:', error);
      throw error;
    }
  }

  async function refreshWorkspaces() {
    try {
      const workspaces = await getUserWorkspaces(currentUser.uid);
      setUserWorkspaces(workspaces);
    } catch (error) {
      console.error('Error refreshing workspaces:', error);
    }
  }

  const value = {
    currentWorkspace,
    currentWorkspaceId,
    userWorkspaces,
    loading,
    isAllBrandsView,
    switchWorkspace,
    switchToAllBrands,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    refreshWorkspaces
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}
